import React from 'react';
import { motion } from 'framer-motion';
import { Navigation, ArrowUpRight } from 'lucide-react';

const PLACES = [
  { name: 'Tholpetty Wildlife Sanctuary', distance: '900 m', tag: 'Elephants · Tigers · Leopards' },
  { name: 'Nagarhole Tiger Reserve', distance: '8.8 km', tag: 'Premier tiger safari' },
  { name: 'Iruppu Waterfalls', distance: '13 km', tag: 'Sacred cascade · Brahmagiri' },
  { name: 'Thirunelly Temple', distance: '18 km', tag: 'Ancient Vishnu shrine' },
  { name: 'Kuruva Island', distance: '19 km', tag: 'River reserve · bamboo rafting' },
  { name: 'Kabini', distance: '30 km', tag: 'River safaris' },
  { name: 'Banasura Sagar Dam', distance: '43 km', tag: 'India\'s largest earthen dam' },
  { name: 'Vythri View Point', distance: '54 km', tag: 'Panoramic Western Ghats' },
];

const SightseeingSection = () => {
  return (
    <section id="sightseeing" className="relative bg-[#0B0F0E] py-32 md:py-44 overflow-hidden" data-testid="sightseeing-section">

      <div className="max-w-[1480px] mx-auto px-6 md:px-12">

        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 lg:col-span-3">
            <div className="overline">Chapter V</div>
            <div className="numeral text-7xl md:text-8xl text-[#D4AF37]/30 mt-4">05</div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-[#E8E3D9] leading-[1.05]"
            >
              The trail<br/>
              <span className="italic text-[#D4AF37]">around us.</span>
            </motion.h2>
            <p className="mt-6 max-w-xl text-[#8E9A95] text-[15px] leading-relaxed">
              We are the still point. Within an hour: tigers, waterfalls, ancient temples and rivers.
              Distances are measured from Tuskermark — go in any direction and you will find the wild.
            </p>
          </div>
        </div>

        {/* trail list */}
        <div className="block lg:grid lg:grid-cols-12 lg:gap-10">
          {/* left: vertical trail */}
          <div className="lg:col-span-8 min-w-0">
            <div className="relative">
              {PLACES.map((p, i) => (
                <motion.a
                  key={i}
                  href={`https://www.google.com/maps/search/${encodeURIComponent(p.name + ' Wayanad Kerala')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="group flex items-center gap-3 md:gap-6 py-6 border-b border-[#D4AF37]/15 last:border-0 hover:bg-[#D4AF37]/[0.03] transition-colors px-1 md:px-2"
                  data-testid={`sightseeing-${i}`}
                >
                  {/* trail marker */}
                  <div className="flex flex-col items-center">
                    <div className="trail-dot" />
                    {i < PLACES.length - 1 && <div className="trail-line h-6 md:h-8 mt-2" />}
                  </div>

                  {/* index */}
                  <div className="numeral text-xl md:text-3xl text-[#D4AF37]/40 w-7 md:w-12 flex-shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </div>

                  {/* name + tag */}
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg md:text-2xl text-[#E8E3D9] group-hover:text-[#D4AF37] transition-colors leading-tight">
                      {p.name}
                    </div>
                    <div className="text-[10px] md:text-[11px] tracking-[0.18em] uppercase text-[#8E9A95] mt-1.5">
                      {p.tag}
                    </div>
                  </div>

                  {/* distance + arrow */}
                  <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="numeral text-base md:text-xl text-[#D4AF37]">{p.distance}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#8E9A95]">distance</div>
                    </div>
                    <ArrowUpRight size={20} className="hidden md:block text-[#D4AF37] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                  </div>
                </motion.a>
              ))}
            </div>
          </div>

          {/* right: sticky info panel */}
          <aside className="lg:col-span-4 min-w-0 mt-10 lg:mt-0">
            <div className="lg:sticky lg:top-32 space-y-6">
              <div className="border border-[#D4AF37]/30 p-6 md:p-8 bg-[#121816]">
                <div className="overline mb-4">Compass point</div>
                <div className="font-serif text-2xl md:text-3xl text-[#E8E3D9] leading-tight mb-3 italic">
                  11°55'N · 75°59'E
                </div>
                <p className="text-[13px] text-[#8E9A95] leading-relaxed">
                  Tholpetty, Wayanad, Kerala — at the meeting of three sanctuaries.
                  Drive 2 hours from Mysuru airport, or 4 from Calicut.
                </p>
                <a
                  href="https://www.google.com/maps/search/Tuskermark+Safari+Retreat+Tholpetty+Kerala"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 btn-brass w-full justify-center"
                  data-testid="sightseeing-directions-btn"
                >
                  <Navigation size={14} />
                  Open Maps
                </a>
              </div>

              <div className="border border-[#D4AF37]/15 p-6 md:p-7 bg-[#121816]">
                <div className="overline mb-3">Note from the resort</div>
                <p className="text-[13px] text-[#C4BFB1] leading-relaxed italic">
                  "Most of our guests come for the sanctuary, then discover Wayanad.
                  We pack a thermos and a route map — yours is on the house."
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default SightseeingSection;
